import type { ReactNode } from 'react';
import {
  ATTIVITA,
  DIPENDENTI,
  NAZIONE_ITALIA,
  NAZIONI,
  ORDINI_PROFESSIONALI,
  QUALIFICHE,
  TESTI_CONSENSO,
  TITOLI,
  type CampoModulo,
  type ErroriModulo,
  type ModuloIscrizione,
} from '@corsi/modulo.ts';
import { REGIONI, provinceDi } from '@corsi/regioniProvince.ts';
import { AvvisoAttestato, Campo, CasellaTesto, Menu, NotaObbligatori, SceltaSiNo, Sezione } from './CampiModulo';
import { idCampo } from './idCampo';
import { INFORMATIVA_PRIVACY } from './informativa';

/**
 * I campi del modulo di iscrizione (dati, crediti, consensi), gli stessi per
 * il modulo corsi e per la registrazione dei biglietti degli eventi. Le
 * regole stanno in @corsi/modulo.ts, le stesse che usano le funzioni.
 * Il <form>, il pulsante e la trappola per i robot li mette la pagina.
 */

/** L'ordine dei campi sulla pagina: serve per portare chi compila al primo errore. */
const ORDINE_CAMPI: readonly CampoModulo[] = [
  'email', 'ripetiEmail', 'nome', 'cognome', 'dataNascita', 'luogoNascita', 'codiceFiscale', 'titolo',
  'azienda', 'cellulare', 'qualifica', 'attivita', 'dipendenti', 'nazione', 'regione', 'provincia', 'citta',
  'cap', 'indirizzo', 'cfp', 'ordine', 'provinciaOrdine', 'numeroIscrizione', 'ore81', 'privacy',
  'consensoInformazioni', 'consensoPartner', 'consensoProfilazione',
];

/** Tutte le province in ordine alfabetico, per la sede dell'ordine professionale. */
const TUTTE_LE_PROVINCE = REGIONI.flatMap((r) => r.province)
  .slice()
  .sort((a, b) => a.nome.localeCompare(b.nome, 'it'))
  .map((p) => ({ valore: p.sigla, testo: `${p.nome} (${p.sigla})` }));

export function portaAlPrimoErrore(errori: ErroriModulo): void {
  const primo = ORDINE_CAMPI.find((campo) => errori[campo]);
  if (!primo) return;
  const elemento = document.getElementById(idCampo(primo));
  elemento?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  elemento?.focus({ preventScroll: true });
}

/** Cambiare regione svuota la provincia; uscire dall'Italia svuota entrambe. */
export function azzeramentiCollegati(campo: CampoModulo, valore: unknown): Partial<ModuloIscrizione> {
  if (campo === 'regione') return { provincia: '' };
  if (campo === 'nazione' && valore !== NAZIONE_ITALIA) return { regione: '', provincia: '' };
  return {};
}

type Props = {
  modulo: ModuloIscrizione;
  errori: ErroriModulo;
  /** yyyy-mm-dd, il massimo della data di nascita. */
  oggi: string;
  aggiorna: <K extends CampoModulo>(campo: K, valore: ModuloIscrizione[K]) => void;
  /** Qualcosa da mostrare in cima, prima dei campi (es. "Il biglietto e' per me"). */
  inCima?: ReactNode;
};

export function CampiIscrizione({ modulo, errori, oggi, aggiorna, inCima }: Props) {
  const inItalia = modulo.nazione === NAZIONE_ITALIA;
  return (
    <>
      {inCima}
    <Sezione titolo="Dati account">
      <NotaObbligatori />
      <Campo campo="email" etichetta="Email" errore={errori.email}>
        <CasellaTesto campo="email" type="email" inputMode="email" autoComplete="email" valore={modulo.email} errore={errori.email} onCambia={(v) => aggiorna('email', v)} />
      </Campo>
      <Campo campo="ripetiEmail" etichetta="Ripeti Email" errore={errori.ripetiEmail}>
        <CasellaTesto campo="ripetiEmail" type="email" inputMode="email" autoComplete="off" valore={modulo.ripetiEmail} errore={errori.ripetiEmail} onCambia={(v) => aggiorna('ripetiEmail', v)} />
      </Campo>
    </Sezione>

    <Sezione titolo="Dati anagrafici">
      <NotaObbligatori />
      <Campo campo="nome" etichetta="Nome" errore={errori.nome}>
        <CasellaTesto campo="nome" autoComplete="given-name" valore={modulo.nome} errore={errori.nome} onCambia={(v) => aggiorna('nome', v)} />
      </Campo>
      <Campo campo="cognome" etichetta="Cognome" errore={errori.cognome}>
        <CasellaTesto campo="cognome" autoComplete="family-name" valore={modulo.cognome} errore={errori.cognome} onCambia={(v) => aggiorna('cognome', v)} />
      </Campo>
      <Campo campo="dataNascita" etichetta="Data di nascita" errore={errori.dataNascita}>
        <CasellaTesto campo="dataNascita" type="date" autoComplete="bday" min="1920-01-01" max={oggi} valore={modulo.dataNascita} errore={errori.dataNascita} onCambia={(v) => aggiorna('dataNascita', v)} />
      </Campo>
      <Campo campo="luogoNascita" etichetta="Luogo di nascita" errore={errori.luogoNascita}>
        <CasellaTesto campo="luogoNascita" placeholder="Comune (o Stato estero)" valore={modulo.luogoNascita} errore={errori.luogoNascita} onCambia={(v) => aggiorna('luogoNascita', v)} />
      </Campo>
      <Campo campo="codiceFiscale" etichetta="Codice fiscale" obbligatorio={inItalia} errore={errori.codiceFiscale}>
        <CasellaTesto
          campo="codiceFiscale"
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          maxLength={16}
          className="uppercase tracking-wider"
          valore={modulo.codiceFiscale}
          errore={errori.codiceFiscale}
          onCambia={(v) => aggiorna('codiceFiscale', v.toUpperCase().replace(/\s+/g, ''))}
        />
      </Campo>
      <Campo campo="titolo" etichetta="Titolo" errore={errori.titolo}>
        <Menu campo="titolo" voci={TITOLI} valore={modulo.titolo} errore={errori.titolo} onCambia={(v) => aggiorna('titolo', v)} />
      </Campo>
      <Campo campo="azienda" etichetta="Azienda/Ente" errore={errori.azienda}>
        <CasellaTesto campo="azienda" autoComplete="organization" valore={modulo.azienda} errore={errori.azienda} onCambia={(v) => aggiorna('azienda', v)} />
      </Campo>
      <Campo campo="cellulare" etichetta="Cellulare" errore={errori.cellulare}>
        <CasellaTesto campo="cellulare" type="tel" inputMode="tel" autoComplete="tel" valore={modulo.cellulare} errore={errori.cellulare} onCambia={(v) => aggiorna('cellulare', v)} />
      </Campo>
      <Campo campo="qualifica" etichetta="Qualifica" errore={errori.qualifica}>
        <Menu campo="qualifica" voci={QUALIFICHE} valore={modulo.qualifica} errore={errori.qualifica} onCambia={(v) => aggiorna('qualifica', v)} />
      </Campo>
      <Campo campo="attivita" etichetta="Attività" errore={errori.attivita}>
        <Menu campo="attivita" voci={ATTIVITA} valore={modulo.attivita} errore={errori.attivita} onCambia={(v) => aggiorna('attivita', v)} />
      </Campo>
      <Campo campo="dipendenti" etichetta="Dipendenti" errore={errori.dipendenti}>
        <Menu campo="dipendenti" voci={DIPENDENTI} valore={modulo.dipendenti} errore={errori.dipendenti} onCambia={(v) => aggiorna('dipendenti', v)} />
      </Campo>
      <Campo campo="nazione" etichetta="Nazione" errore={errori.nazione}>
        <Menu campo="nazione" voci={NAZIONI} valore={modulo.nazione} errore={errori.nazione} onCambia={(v) => aggiorna('nazione', v)} />
      </Campo>
      {inItalia && (
        <>
          <Campo campo="regione" etichetta="Regione" errore={errori.regione}>
            <Menu campo="regione" voci={REGIONI.map((r) => r.nome)} valore={modulo.regione} errore={errori.regione} onCambia={(v) => aggiorna('regione', v)} />
          </Campo>
          <Campo campo="provincia" etichetta="Provincia" errore={errori.provincia}>
            <Menu
              campo="provincia"
              voci={provinceDi(modulo.regione).map((p) => ({ valore: p.sigla, testo: p.nome }))}
              segnaposto={modulo.regione ? 'Seleziona' : 'Scegli prima la regione'}
              disabilitato={!modulo.regione}
              valore={modulo.provincia}
              errore={errori.provincia}
              onCambia={(v) => aggiorna('provincia', v)}
            />
          </Campo>
        </>
      )}
      <Campo campo="citta" etichetta="Città" errore={errori.citta}>
        <CasellaTesto campo="citta" autoComplete="address-level2" valore={modulo.citta} errore={errori.citta} onCambia={(v) => aggiorna('citta', v)} />
      </Campo>
      <Campo campo="cap" etichetta="CAP" errore={errori.cap}>
        <CasellaTesto campo="cap" inputMode={inItalia ? 'numeric' : 'text'} autoComplete="postal-code" maxLength={inItalia ? 5 : 10} valore={modulo.cap} errore={errori.cap} onCambia={(v) => aggiorna('cap', v)} />
      </Campo>
      <Campo campo="indirizzo" etichetta="Indirizzo" errore={errori.indirizzo}>
        <CasellaTesto campo="indirizzo" autoComplete="street-address" placeholder="Via e numero civico" valore={modulo.indirizzo} errore={errori.indirizzo} onCambia={(v) => aggiorna('indirizzo', v)} />
      </Campo>
    </Sezione>

    <Sezione titolo="Vuoi richiedere i crediti CFP [ove previsti] ?">
      <SceltaSiNo campo="cfp" valore={modulo.cfp} errore={errori.cfp} onCambia={(v) => aggiorna('cfp', v)} />
      {modulo.cfp === 'no' && <AvvisoAttestato cosa="CFP" />}
      {modulo.cfp === 'si' && (
        <div className="mb-2 rounded border-l-4 border-primary-500 bg-white/90 px-4 pb-1 pt-3">
          <p className="mb-3 text-base text-gray-700">Per riconoscerti i crediti ci servono i dati del tuo ordine o collegio.</p>
          <Campo campo="ordine" etichetta="Ordine/Collegio professionale" errore={errori.ordine}>
            <Menu campo="ordine" voci={ORDINI_PROFESSIONALI} valore={modulo.ordine} errore={errori.ordine} onCambia={(v) => aggiorna('ordine', v)} />
          </Campo>
          <Campo campo="provinciaOrdine" etichetta="Provincia dell'Ordine/Collegio" errore={errori.provinciaOrdine}>
            <Menu campo="provinciaOrdine" voci={TUTTE_LE_PROVINCE} valore={modulo.provinciaOrdine} errore={errori.provinciaOrdine} onCambia={(v) => aggiorna('provinciaOrdine', v)} />
          </Campo>
          <Campo campo="numeroIscrizione" etichetta="Numero di iscrizione" errore={errori.numeroIscrizione}>
            <CasellaTesto campo="numeroIscrizione" autoComplete="off" maxLength={20} valore={modulo.numeroIscrizione} errore={errori.numeroIscrizione} onCambia={(v) => aggiorna('numeroIscrizione', v)} />
          </Campo>
        </div>
      )}
    </Sezione>

    <Sezione titolo="Vuoi richiedere le ore di aggiornamento 81/08 [ove previste] ?">
      <SceltaSiNo campo="ore81" valore={modulo.ore81} errore={errori.ore81} onCambia={(v) => aggiorna('ore81', v)} />
      {modulo.ore81 === 'no' && <AvvisoAttestato cosa="ORE AGGIORNAMENTO 81/08" />}
    </Sezione>

    <Sezione titolo="Consenso al trattamento dei dati personali (UE) 2016/679">
      <div className="my-3">
        <label className="flex cursor-pointer items-start gap-3 text-lg leading-relaxed text-gray-900">
          <input
            id={idCampo('privacy')}
            type="checkbox"
            checked={modulo.privacy}
            onChange={(e) => aggiorna('privacy', e.target.checked)}
            aria-invalid={Boolean(errori.privacy)}
            className="mt-1.5 h-6 w-6 flex-shrink-0 cursor-pointer accent-primary-600"
          />
          <span>
            Dichiaro di aver letto e capito l'
            <a href={INFORMATIVA_PRIVACY} target="_blank" rel="noopener noreferrer" className="text-primary-700 underline underline-offset-2">
              informativa sulla privacy
            </a>
            <span className="ml-1 text-red-600">*</span>
          </span>
        </label>
        {errori.privacy && <p role="alert" className="mt-1 text-sm font-medium text-red-600">{errori.privacy}</p>}
      </div>

      <SceltaSiNo campo="consensoInformazioni" domanda={TESTI_CONSENSO.informazioni} valore={modulo.consensoInformazioni} errore={errori.consensoInformazioni} onCambia={(v) => aggiorna('consensoInformazioni', v)} />
      <SceltaSiNo campo="consensoPartner" domanda={TESTI_CONSENSO.partner} valore={modulo.consensoPartner} errore={errori.consensoPartner} onCambia={(v) => aggiorna('consensoPartner', v)} />
      <SceltaSiNo campo="consensoProfilazione" domanda={TESTI_CONSENSO.profilazione} valore={modulo.consensoProfilazione} errore={errori.consensoProfilazione} onCambia={(v) => aggiorna('consensoProfilazione', v)} />
      <p className="text-sm text-gray-500">I consensi facoltativi non sono necessari per iscriversi e si possono ritirare in qualsiasi momento.</p>
    </Sezione>
    </>
  );
}
